import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';
import { ApiResponseDisplay } from '../ApiResponseDisplay';

interface SuccessScreenProps {
  screen: Screen;
  onNext?: () => void;
}

export const SuccessScreen: React.FC<SuccessScreenProps> = ({ screen, onNext }) => {
  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Success */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            <div className="p-6 h-full flex flex-col items-center justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3, type: "spring" }}
                className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6"
              >
                <CheckCircle className="w-10 h-10 text-blue-600" />
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="text-2xl font-bold text-black mb-2"
              >
                {screen.welcomeMessage}
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="text-sm text-gray-600 text-center mb-8"
              >
                {screen.activationMessage}
              </motion.p>

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.9 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onNext}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold cursor-pointer"
              >
                Continue
              </motion.button>
            </div>
          </PhoneFrame>
        </motion.div>

        {/* Right Side - API Response */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {screen.description}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              {screen.steps?.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex items-center space-x-2">
                    <div className={`step-dot ${step.status}`} />
                    <span className={`text-sm font-medium ${
                      step.status === 'active' ? 'text-blue-600' : 
                      step.status === 'completed' ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {index < (screen.steps?.length || 0) - 1 && (
                    <div className="w-8 h-px bg-gray-300" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* API Response */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex space-x-2">
                <div className="text-sm font-medium text-gray-600">
                  &lt;/&gt; Code
                </div>
              </div>
            </div>

            <ApiResponseDisplay apiResponse={screen.apiResponse} />
          </div>
        </motion.div>
      </div>
    </div>
  );
};
